import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Rocket, X, ArrowRight, PartyPopper } from "lucide-react";
import { api } from "../lib/api";

const has = (x) => (x || []).length > 0;

export const isInvited = (p) => p?.plan_source === "invite" || !!p?.invite_id;

export function stepsFor(p, adverts) {
  const s = [
    { key: "account", title: "Create your account", why: "Done. Welcome aboard!", req: true, done: true },
    { key: "business", title: "Add your business details", why: "Your business name, type and contact number.", req: true, done: !!p?.id && !!p.business_name },
    { key: "services", title: "Choose the services you offer", why: "So we can match you to the right jobs and searches.", req: true, done: has(p?.categories) },
    { key: "area", title: "Set your location and service area", why: "Homeowners near you will find you in search.", req: true, done: !!p?.lat },
    { key: "about", title: "Describe your business", why: "A few lines about your experience and what makes you different (at least 50 characters).", req: true, done: (p?.description || "").trim().length >= 50 },
    { key: "advert", title: "Create your first service advert", why: "A ready-made service with a clear price that homeowners can request or book.", req: true, done: adverts > 0 },
    { key: "logo", title: "Upload your logo", why: "Profiles with a logo get noticed more.", req: false, done: !!p?.logo },
    { key: "photos", title: "Add 3 photos of your work", why: "Before and after photos work really well.", req: false, done: (p?.gallery || []).length >= 3 },
    { key: "days", title: "Set your working days", why: "Homeowners can only book online on these days.", req: false, done: Array.isArray(p?.working_days) },
  ];
  if (!isInvited(p)) {
    s.push({ key: "docs", title: "Upload insurance or ID", why: "Helps our team approve you faster and earns verified badges.", req: false, done: has(p?.documents) },
      { key: "submit", title: "Submit your profile for approval", why: "Our team reviews new profiles, usually within 1 working day.", req: true, done: ["pending", "approved"].includes(p?.status) });
  }
  return s;
}

export function summarise(steps) {
  const done = steps.filter((s) => s.done).length;
  const reqLeft = steps.filter((s) => s.req && !s.done);
  return { done, total: steps.length, reqLeft: reqLeft.length, next: reqLeft[0] || steps.find((s) => !s.done), pct: Math.round((done / steps.length) * 100) };
}

export function useSetup() {
  const [p, setP] = useState(null);
  const [ads, setAds] = useState(0);
  const reload = useCallback(async () => {
    const me = (await api.get("/provider/me")).data || {};
    const n = me.id ? await api.get("/provider/adverts").then((r) => r.data.adverts.length).catch(() => 0) : 0;
    setAds(n); setP(me);
  }, []);
  useEffect(() => { reload(); }, [reload]);
  return { p, setP, ads, reload };
}

export function ProgressBar({ pct }) {
  return <div className="h-2 bg-slate-200 rounded-full overflow-hidden"><div className="h-full bg-[#FF8166] rounded-full transition-[width] duration-700" style={{ width: `${pct}%` }} data-testid="setup-progress-bar" /></div>;
}

export function SetupCard() {
  const { p, ads } = useSetup();
  const [hidden, setHidden] = useState(false);
  if (!p?.id || hidden || p.checklist_dismissed) return null;
  const sm = summarise(stepsFor(p, ads));
  const isNew = Date.now() - new Date(p.created_at || 0).getTime() < 30 * 864e5;
  if (sm.done === sm.total || (sm.reqLeft === 0 && (!isNew || p.is_demo))) return null;
  const hide = () => { setHidden(true); api.put("/provider/me", { checklist_dismissed: true }); };
  const live = sm.reqLeft === 0;
  return (
    <div className="card p-5 mb-5 border-[#FF8166]/40 bg-[#FFF8F5] fade-up" data-testid="setup-card">
      <div className="flex items-start gap-3">
        {live ? <PartyPopper className="text-[#FF8166] shrink-0" /> : <Rocket className="text-[#FF8166] shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="font-bold text-slate-800" data-testid="setup-card-title">{live ? "The basics are done. A few extras will help you stand out" : "Finish setting up your profile"}</div>
          <div className="text-xs muted mt-0.5" data-testid="setup-card-count">{sm.done} of {sm.total} steps done{sm.next && ` · Next: ${sm.next.title}`}</div>
          <div className="mt-3 max-w-md"><ProgressBar pct={sm.pct} /></div>
        </div>
        <Link to="/provider/onboarding" className="btn-coral btn-sm whitespace-nowrap" data-testid="setup-card-continue">Continue <ArrowRight size={14} /></Link>
        <button onClick={hide} className="text-slate-400 hover:text-slate-600" aria-label="Hide checklist" data-testid="setup-card-dismiss"><X size={16} /></button>
      </div>
    </div>
  );
}
